/**
 * Badge Registry (GRE-114)
 *
 * Unified registry of all badge configurations.
 * Single source of truth for badge display across:
 * - Magic moments (suspension, false relation, picardy, bare fifth)
 * - Chromatic chords (secondary dominants, neapolitan, etc.)
 */

import type { BadgeConfig, HarmonicBadgeType } from '@/types/harmonic-vocabulary';

/**
 * Unified badge registry
 *
 * Colors follow the warm Kinfolk design system:
 * - Magic moments use existing colors from GLOW_COLORS/BADGE_CONFIG
 * - Chromatic chords use harmonizing accent colors
 */
export const BADGE_REGISTRY: Record<HarmonicBadgeType, BadgeConfig> = {
  // ============================================================================
  // Magic Moments (from GRE-111)
  // ============================================================================

  suspension: {
    key: 'suspension',
    text: 'sus',
    bg: '#E8A87C',  // Warm amber
    glow: 'rgba(232, 168, 124, 0.4)',
    category: 'magic-moment',
  },

  false_relation: {
    key: 'false_relation',
    text: '!',
    bg: '#9B7EBD',  // Soft purple
    glow: 'rgba(155, 126, 189, 0.4)',
    category: 'magic-moment',
  },

  picardy: {
    key: 'picardy',
    text: '+',
    bg: '#D4AF37',  // Gold
    glow: 'rgba(212, 175, 55, 0.5)',
    category: 'magic-moment',
  },

  bare_fifth: {
    key: 'bare_fifth',
    text: '5',
    bg: '#6C6C6C',  // Neutral gray
    glow: 'rgba(108, 108, 108, 0.3)',
    category: 'magic-moment',
  },

  // ============================================================================
  // Chromatic Chords (GRE-114)
  // ============================================================================

  secondary_dominant: {
    key: 'secondary_dominant',
    text: 'V/',
    bg: '#E85D3D',  // Warm terracotta (from design system)
    glow: 'rgba(232, 93, 61, 0.4)',
    category: 'chromatic',
  },

  secondary_leading_tone: {
    key: 'secondary_leading_tone',
    text: 'vii°/',
    bg: '#C44D2E',  // Darker terracotta
    glow: 'rgba(196, 77, 46, 0.4)',
    category: 'chromatic',
  },

  neapolitan: {
    key: 'neapolitan',
    text: '♭II',
    bg: '#5D9B9B',  // Teal (complementary to warm colors)
    glow: 'rgba(93, 155, 155, 0.4)',
    category: 'chromatic',
  },

  augmented_sixth: {
    key: 'augmented_sixth',
    text: '+6',
    bg: '#8B5CF6',  // Violet
    glow: 'rgba(139, 92, 246, 0.4)',
    category: 'chromatic',
  },

  borrowed: {
    key: 'borrowed',
    text: 'bor',
    bg: '#6B7280',  // Cool gray
    glow: 'rgba(107, 114, 128, 0.3)',
    category: 'chromatic',
  },

  chromatic_mediant: {
    key: 'chromatic_mediant',
    text: 'chr',
    bg: '#10B981',  // Emerald green
    glow: 'rgba(16, 185, 129, 0.4)',
    category: 'chromatic',
  },

  // ============================================================================
  // Extension Chords (GRE-114)
  // ============================================================================

  // 9th Chords
  dom9: {
    key: 'dom9',
    text: '9',
    bg: '#E8A03E',  // Warm gold
    category: 'extension',
  },

  maj9: {
    key: 'maj9',
    text: '△9',
    bg: '#7DA87B',  // Soft sage
    category: 'extension',
  },

  min9: {
    key: 'min9',
    text: 'm9',
    bg: '#8B9DC3',  // Slate blue
    category: 'extension',
  },

  // 11th Chords
  dom11: {
    key: 'dom11',
    text: '11',
    bg: '#C9A66B',  // Muted gold
    category: 'extension',
  },

  min11: {
    key: 'min11',
    text: 'm11',
    bg: '#7B8FA8',  // Steel blue
    category: 'extension',
  },

  // 13th Chords
  dom13: {
    key: 'dom13',
    text: '13',
    bg: '#D4A574',  // Warm tan
    category: 'extension',
  },

  maj13: {
    key: 'maj13',
    text: '△13',
    bg: '#6B9080',  // Sage green
    category: 'extension',
  },

  min13: {
    key: 'min13',
    text: 'm13',
    bg: '#6B7B8C',  // Cool slate
    category: 'extension',
  },

  // Altered Chords
  alt: {
    key: 'alt',
    text: 'alt',
    bg: '#B97A57',  // Burnt sienna
    category: 'extension',
  },

  dom7b9: {
    key: 'dom7b9',
    text: '7♭9',
    bg: '#A86B5A',  // Dusty rose
    category: 'extension',
  },

  dom7sharp9: {
    key: 'dom7sharp9',
    text: '7♯9',
    bg: '#9B6B6B',  // Mauve
    category: 'extension',
  },

  dom7sharp11: {
    key: 'dom7sharp11',
    text: '7♯11',
    bg: '#8B7B5A',  // Olive
    category: 'extension',
  },

  // Added Tones
  add9: {
    key: 'add9',
    text: '+9',
    bg: '#D4AF37',  // Gold (shimmer)
    category: 'extension',
  },

  add11: {
    key: 'add11',
    text: '+11',
    bg: '#6B9080',  // Sage
    category: 'extension',
  },

  add13: {
    key: 'add13',
    text: '+13',
    bg: '#9B8B6B',  // Warm gray
    category: 'extension',
  },

  // Suspensions (as chord quality)
  sus2: {
    key: 'sus2',
    text: 'sus2',
    bg: '#C9B896',  // Warm sand
    category: 'extension',
  },

  sus4: {
    key: 'sus4',
    text: 'sus4',
    bg: '#E8A87C',  // Warm amber (matches suspension magic moment)
    category: 'extension',
  },
};

/**
 * Get badge configuration for a harmonic feature
 */
export function getBadgeConfig(type: HarmonicBadgeType): BadgeConfig {
  return BADGE_REGISTRY[type];
}

/**
 * Get all magic moment badges
 */
export function getMagicMomentBadges(): BadgeConfig[] {
  return Object.values(BADGE_REGISTRY).filter(b => b.category === 'magic-moment');
}

/**
 * Get all chromatic badges
 */
export function getChromaticBadges(): BadgeConfig[] {
  return Object.values(BADGE_REGISTRY).filter(b => b.category === 'chromatic');
}

/**
 * Get all extension badges (GRE-114)
 */
export function getExtensionBadges(): BadgeConfig[] {
  return Object.values(BADGE_REGISTRY).filter(b => b.category === 'extension');
}

/**
 * Check if a badge type is an extension
 */
export function isExtensionBadge(type: HarmonicBadgeType): boolean {
  const config = BADGE_REGISTRY[type];
  return config?.category === 'extension';
}
